import { useModal } from "@circle-react/components/Layout/ModalManagerProvider";
import { CurrentPostContextProvider } from "@circle-react/contexts/currentPostContext";
import { CurrentSpaceContextProvider } from "@circle-react/contexts/currentSpaceContext";
import { PostsDetailsContextProvider } from "@circle-react/contexts/postsDetailsContext";
import { noop } from "lodash";

import { t } from "@/i18n-js/instance";
import { AdjustImages } from "@/react/components/Spaces/ImageSpace/ImagePostModal/AdjustImages";
import { STEPS } from "@/react/components/Spaces/ImageSpace/ImagePostModal/constants";
import { CreatePost } from "@/react/components/Spaces/ImageSpace/ImagePostModal/CreatePost";
import { useInitializeStore } from "@/react/components/Spaces/ImageSpace/ImagePostModal/hooks/useInitializeStore";
import { usePost } from "@/react/components/Spaces/ImageSpace/ImagePostModal/hooks/usePost";
import { useImagePostModalStore } from "@/react/components/Spaces/ImageSpace/ImagePostModal/store";
import { UploadImages } from "@/react/components/Spaces/ImageSpace/ImagePostModal/UploadImages";

import type { ImagePostModalProps } from "./types";

export const ImagePostModalUIKitComponent = ({
  spaceId,
  onClose = noop,
  currentPost,
  onPostChange = noop,
  shouldSkipRedirectAfterSubmit = false,
}: ImagePostModalProps) => {
  const modal = useModal();
  const { currentStep, setCurrentStep, reset, isFormDirty, isImagesDirty } =
    useImagePostModalStore();
  const onModalClose = (skipDirtyCheck: boolean = false) => {
    if (
      skipDirtyCheck ||
      !(isFormDirty || isImagesDirty) ||
      confirm(t("quick_post_v2.accidental_leave_confirmation"))
    ) {
      reset();
      onClose();
    }
  };
  const post = usePost(currentPost);

  useInitializeStore(post);

  if (!modal || !modal.visible || !post) return null;

  const perPagePostIds = post.id ? [[post.id]] : [];

  return (
    <CurrentSpaceContextProvider spaceId={spaceId}>
      <PostsDetailsContextProvider perPagePostIds={perPagePostIds}>
        <CurrentPostContextProvider post={post}>
          {currentStep === STEPS.UPLOAD_IMAGES && (
            <UploadImages
              onClose={onModalClose}
              onSelection={() => setCurrentStep(STEPS.ADJUST_IMAGES)}
            />
          )}

          {currentStep === STEPS.ADJUST_IMAGES && (
            <AdjustImages onClose={onModalClose} />
          )}

          {currentStep !== STEPS.UPLOAD_IMAGES &&
            currentStep !== STEPS.ADJUST_IMAGES && (
              <CreatePost
                onClose={onModalClose}
                onPostChange={onPostChange}
                shouldSkipRedirectAfterSubmit={shouldSkipRedirectAfterSubmit}
              />
            )}
        </CurrentPostContextProvider>
      </PostsDetailsContextProvider>
    </CurrentSpaceContextProvider>
  );
};
